import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { DateRange } from 'react-day-picker';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { SortByDate } from '../../types/events.types';
import { SafeOrder, SafeSortBy, SafeWithPermissions } from '../../types/safes.types';
import { dateFilterVariant } from '../event/index';
import { useAppQuery } from '../use-app-query.hook';
import { useFormat } from '../use-format.hook';

export type GetSafesResponse = {
  data: SafeWithPermissions[];
  totalPages: number;
  totalSafesCount: number;
};

export const useSafes = ({
  companyId,
  numberOfFetchedItems = 10,
  sortBy = SafeSortBy.FULL_NAME,
  currentPage = 1,
  search = '',
  sortOrder = SafeOrder.ASC,
  convertedOnly,
  customDateFilter,
  dateFilter = SortByDate.ALL_TIME,
  enabled = true,
}: {
  companyId: string;
  convertedOnly?: boolean;
  currentPage?: number;
  customDateFilter?: DateRange;
  dateFilter?: SortByDate;
  enabled?: boolean;
  numberOfFetchedItems?: number;
  search?: string;
  sortBy?: null | SafeSortBy;
  sortOrder?: null | SafeOrder;
}) => {
  const { format } = useFormat();

  const queryClient = useQueryClient();
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * numberOfFetchedItems;
  const dateRange = dateFilterVariant[dateFilter]({
    from: customDateFilter?.from,
    to: customDateFilter?.to,
  });

  const dateRangeTo = dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined;
  const dateRangeFrom = dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined;

  const {
    data: { data, totalPages, totalSafesCount } = {},
    isLoading,
    refetch,
  } = useAppQuery<GetSafesResponse, HttpException>({
    queryKey: [
      QueryKey.GET_SAFES,
      companyId,
      {
        to: dateRangeTo,
        from: dateRangeFrom,
        page: currentPage,
        sortBy,
        sortOrder,
        skip,
        numberOfFetchedItems,
        convertedOnly,
      },
    ],
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'safes'] },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip,
            search,
            'start-date': dateRangeFrom,
            'end-date': dateRangeTo,
            convertedOnly: convertedOnly,
            ...(sortOrder && sortBy ? { sortBy, sortOrder } : {}),
          },
        },
      ),
    enabled: !!companyId && enabled,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SAFES] });
  }, [queryClient]);

  return {
    invalidateQuery,
    safes: data,
    totalPages,
    totalSafesCount,
    isLoading,
    refetch,
    isEmpty: data?.length === 0,
    isSearchResultEmpty: data?.length === 0 && dateFilter !== SortByDate.ALL_TIME,
  };
};
